import type { Block, BlockType, TemplateContent } from "@aswin.dev/types";
import { createBlock, createCustomBlock, isSection } from "@aswin.dev/types";
import { computed, inject } from "vue";
import {
  BLOCK_DEFAULTS_KEY,
  CAPABILITIES_KEY,
  CUSTOM_BLOCK_DEFINITIONS_KEY,
  EDITOR_KEY,
  EDITOR_TYPE_KEY,
} from "../keys";
import { getBlockTypeLabel } from "../utils/blockTypeLabels";
import { templateHasFormBlock } from "../utils/discountCouponFlow";
import { useI18n } from "./useI18n";

// Popups use the Form block (multi-field opt-in) instead of the single
// `input` field, so hide it from the palette there.
const POPUP_HIDDEN_BLOCK_TYPES = new Set<string>([
  "social",
  "menu",
  "table",
  "input",
]);

// The Form block is currently popup-only (it ships sync integrations and a
// multi-step submit flow that don't apply to email exports). Hide it from
// non-popup palettes for now.
const NON_POPUP_HIDDEN_BLOCK_TYPES = new Set<string>(["form"]);

/** True if `blocks` (active canvas / root) contains a discount coupon nested in sections. */
export function canvasTreeHasDiscountCouponBlock(
  blocks: Block[] | null | undefined,
): boolean {
  if (!blocks?.length) return false;
  for (const block of blocks) {
    if (block.type === "discountCoupon") return true;
    if (isSection(block)) {
      for (const column of block.children) {
        if (!Array.isArray(column)) continue;
        if (canvasTreeHasDiscountCouponBlock(column)) return true;
      }
    }
  }
  return false;
}

/** True if any canvas page / root blocks tree contains a discount coupon block. */
export function templateHasDiscountCouponBlock(
  content: TemplateContent | null | undefined,
): boolean {
  if (!content) return false;
  const pages = content.canvasPages;
  if (pages?.length) {
    for (const page of pages) {
      if (canvasTreeHasDiscountCouponBlock(page.blocks)) return true;
    }
    return false;
  }
  return canvasTreeHasDiscountCouponBlock(content.blocks);
}

export interface BlockPaletteItem {
  type: BlockType | string;
  label: string;
  isCustom?: boolean;
  icon?: string;
}

export function useBlockPalette() {
  const { t, format } = useI18n();
  const customBlockDefinitions = inject(CUSTOM_BLOCK_DEFINITIONS_KEY, []);
  const blockDefaults = inject(BLOCK_DEFAULTS_KEY, undefined);
  const editor = inject(EDITOR_KEY, null);
  const caps = inject(CAPABILITIES_KEY, {});
  const editorType = inject(EDITOR_TYPE_KEY, "email");

  const showModulesSection = computed(
    () => (caps.savedModules?.moduleCount.value ?? 0) > 0,
  );

  const builtInBlockTypeOrder: string[] = [
    "section",
    "image",
    "title",
    "paragraph",
    "button",
    "discountCoupon",
    "form",
    "input",
    "divider",
    "video",
    "social",
    "menu",
    "table",
    "countdown",
    "spacer",
    "html",
  ];

  const builtInBlockTypes = computed<BlockPaletteItem[]>(() => {
    const hidden =
      editorType === "popup"
        ? POPUP_HIDDEN_BLOCK_TYPES
        : NON_POPUP_HIDDEN_BLOCK_TYPES;
    const order = builtInBlockTypeOrder.filter((type) => !hidden.has(type));

    return order.map((type) => ({
      type,
      label: getBlockTypeLabel(type, t),
    }));
  });

  const customBlockItems = computed<BlockPaletteItem[]>(() => {
    return customBlockDefinitions.map((def) => ({
      type: `custom:${def.type}`,
      label: def.name,
      isCustom: true,
      icon: def.icon,
    }));
  });

  const blockTypes = computed<BlockPaletteItem[]>(() => [
    ...builtInBlockTypes.value,
    ...customBlockItems.value,
  ]);

  const discountCouponTakenInTemplate = computed(() =>
    editor ? templateHasDiscountCouponBlock(editor.content.value) : false,
  );

  const formTakenInTemplate = computed(() =>
    editor ? templateHasFormBlock(editor.content.value) : false,
  );

  /** Sidebar row blocked when template already contains that singleton block type. */
  function paletteRowBlocked(item: BlockPaletteItem): boolean {
    if (item.isCustom) return false;
    if (item.type === "discountCoupon")
      return discountCouponTakenInTemplate.value;
    if (item.type === "form") return formTakenInTemplate.value;
    return false;
  }

  /** Tooltip / aria hint when {@link paletteRowBlocked} is true. */
  function paletteBlockedReason(item: BlockPaletteItem): string | null {
    if (!paletteRowBlocked(item)) return null;
    if (item.type === "form") return t.sidebarNav.paletteFormTaken;
    return t.sidebarNav.paletteDiscountCouponTaken;
  }

  function createBlockFromItem(item: BlockPaletteItem): Block {
    if (item.isCustom) {
      const customType = item.type.replace("custom:", "");
      const definition = customBlockDefinitions.find(
        (d) => d.type === customType,
      );
      if (definition) {
        return createCustomBlock(definition);
      }
    }

    return createBlock(item.type as BlockType, blockDefaults);
  }

  function insertBlockFromItem(item: BlockPaletteItem): void {
    if (!editor || paletteRowBlocked(item)) return;
    const block = createBlockFromItem(item);
    editor.addBlock(block);
    editor.selectBlock(block.id);
  }

  function handlePaletteKeydown(
    event: KeyboardEvent,
    item: BlockPaletteItem,
  ): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!paletteRowBlocked(item)) insertBlockFromItem(item);
    }
  }

  return {
    t,
    format,
    blockTypes,
    createBlockFromItem,
    insertBlockFromItem,
    handlePaletteKeydown,
    paletteRowBlocked,
    paletteBlockedReason,
    caps,
    showModulesSection,
  };
}
